/**
 * RTMPose ONNX Runtime Web session loader.
 *
 * Picks the best available execution provider:
 *   - WebGPU when navigator.gpu is present and the user agent isn't
 *     known to be broken (Safari 17 has WebGPU but ort-web's WebGPU
 *     path crashes there as of 1.20).
 *   - wasm-simd as fallback (works everywhere ort-web works).
 *
 * Tier (`t/m/l`) selects the model variant. Mobile callers SHOULD pass
 * `t`; PC callers can request `l` for highest accuracy.
 */

import * as ort from 'onnxruntime-web';

import {
  ensureOrtWasmEnv,
  fetchModelCached,
  pickOnnxEPs,
  type OnnxExecutionProvider,
} from '../onnx-runtime';

export type RtmPoseTier = 't' | 'm' | 'l';
export type RtmPoseDevice = OnnxExecutionProvider;

export interface RtmPoseLoaderOpts {
  tier: RtmPoseTier;
  /** Override execution provider. Auto-detected when omitted. */
  device?: RtmPoseDevice;
  /** Override model URL. Useful for self-hosting. */
  modelUrl?: string;
}

export interface RtmPoseSession {
  session: ort.InferenceSession;
  inputName: string;
  /** Most RTMPose ONNX exports emit two outputs (simcc_x, simcc_y). */
  outputNames: readonly string[];
  inputWidth: number;
  inputHeight: number;
  device: RtmPoseDevice;
}

const DEFAULT_MODEL_URLS: Readonly<Record<RtmPoseTier, string>> = {
  // Self-host these in /public/models/ before flipping the engine on.
  // The MMPose 1.x ONNX exports include simcc_x / simcc_y outputs.
  t: '/models/rtmpose-t-coco17.onnx',
  m: '/models/rtmpose-m-coco17.onnx',
  l: '/models/rtmpose-l-coco17.onnx',
};

const INPUT_DIMS_BY_TIER: Readonly<Record<RtmPoseTier, { width: number; height: number }>> = {
  // RTMPose's canonical input is 192×256 (W×H) for `t/m`, 288×384 for `l`.
  t: { width: 192, height: 256 },
  m: { width: 192, height: 256 },
  l: { width: 288, height: 384 },
};

export async function loadRTMPoseSession(opts: RtmPoseLoaderOpts): Promise<RtmPoseSession> {
  ensureOrtWasmEnv();
  const { picked, list } = pickOnnxEPs({ override: opts.device ?? null });
  const modelUrl = opts.modelUrl ?? DEFAULT_MODEL_URLS[opts.tier];
  const dims = INPUT_DIMS_BY_TIER[opts.tier];

  const sessionOptions: ort.InferenceSession.SessionOptions = {
    executionProviders: list,
    graphOptimizationLevel: 'all',
  };

  const { bytes, diag } = await fetchModelCached(modelUrl);
  console.info(
    '[rtmpose:loader]',
    opts.tier,
    diag.source,
    `${(diag.byteLength / 1e6).toFixed(1)}MB`,
    `${diag.ms.toFixed(0)}ms`,
  );
  const session = await ort.InferenceSession.create(bytes, sessionOptions);
  const inputName = session.inputNames[0];
  const outputNames = [...session.outputNames];

  return {
    session,
    inputName,
    outputNames,
    inputWidth: dims.width,
    inputHeight: dims.height,
    device: picked,
  };
}
